import { NmLoader } from './NmLoader';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoCheckmarkOutline, IoPeopleOutline, IoShieldCheckmarkOutline } from 'react-icons/io5';
import type { UserProfile } from '../services/api';
import { subscriptionApi, type SubscriptionOrder, type SubscriptionPlan, type SubscriptionQuote } from '../services/subscriptionApi';
import { normalizeInputDigits } from '../utils/localizedInput';
import { NmButton } from './NmButton';
import subscriptionCardArt from '../assets/subscription-glass-card.webp';
import './subscription-panel.css';

export function SubscriptionPanel({ user, onActivated }: { user: UserProfile; onActivated?: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const role = user.role === 'coach' ? 'coach' : 'athlete';
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [codeDraft, setCodeDraft] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [quoteRetry, setQuoteRetry] = useState(0);
  const [codeError, setCodeError] = useState('');
  const normalizeCode = (code: string) => normalizeInputDigits(code.trim()).toUpperCase();
  const [quote, setQuote] = useState<SubscriptionQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(false);
  const [orders, setOrders] = useState<SubscriptionOrder[]>([]);
  const [ordersError, setOrdersError] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [created, setCreated] = useState<SubscriptionOrder | null>(null);
  const requestKey = useRef<string | null>(null);
  const submitting = useRef(false);
  const reviewHeading = useRef<HTMLHeadingElement>(null);
  const successHeading = useRef<HTMLHeadingElement>(null);
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const number = (value: number) => Number(value).toLocaleString(locale);
  const duration = (days: number) => t(days === 30 ? 'subscription.monthly' : days === 365 ? 'subscription.yearly' : days === 180 ? 'subscription.halfYear' : days === 90 ? 'subscription.quarterly' : 'subscription.days', { count: days, value: number(days) });
  const selected = plans.find(p => p.id === selectedId);
  const periods = [...new Set(plans.map(p => Number(p.day_count)))].sort((a, b) => a - b);
  const options = plans.filter(p => Number(p.day_count) === Number(selected?.day_count));
  const pending = orders.find(order => Number(order.status) === 2 && Number(order.user_id) === user.id);
  const validQuote = quote?.price_id === selectedId && (!appliedCode || quote.discount_code === appliedCode) ? quote : null;
  const currency = (value: string) => value === 'IRT' ? t('subscription.toman') : value;
  const deadline = (order: SubscriptionOrder) => order.payment_due_at && Number.isFinite(Date.parse(order.payment_due_at))
    ? new Date(order.payment_due_at).toLocaleString(locale) : '';
  const total = (value: number, code: string) => `${number(value)} ${currency(code)}`;

  useEffect(() => {
    const controller = new AbortController();
    setOrdersLoading(true); setLoading(true); setLoadError(false); setOrdersError(false); setQuote(null); setReview(false);
    void subscriptionApi.plans(role, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      const sorted = [...result].sort((a, b) => Number(a.day_count) - Number(b.day_count) || Number(a.price) - Number(b.price));
      setPlans(sorted); setSelectedId(sorted[0]?.id ?? null);
    }).catch(() => { if (!controller.signal.aborted) setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    void subscriptionApi.orders(controller.signal).then(result => {
      if (!controller.signal.aborted) setOrders(result);
    }).catch(() => { if (!controller.signal.aborted) setOrdersError(true); })
      .finally(() => { if (!controller.signal.aborted) setOrdersLoading(false); });
    return () => controller.abort();
  }, [role, user.id, retry]);

  useEffect(() => {
    const controller = new AbortController();
    setCodeError(''); setQuote(null); setQuoteError(false); setReview(false); setPurchaseError(''); requestKey.current = null;
    if (selectedId == null) return () => controller.abort();
    setQuoteLoading(true);
    void subscriptionApi.quote(selectedId, controller.signal, appliedCode).then(result => {
      if (!controller.signal.aborted) setQuote(result);
    }).catch(error => {
      if (controller.signal.aborted) return;
      const reason = error?.response?.data?.errors?.discount_code?.[0];
      if (appliedCode && reason) setCodeError(t(`subscription.couponErrors.${reason}`, { defaultValue: t('subscription.couponUnavailable') }));
      else setQuoteError(true);
    })
      .finally(() => { if (!controller.signal.aborted) setQuoteLoading(false); });
    return () => controller.abort();
  }, [selectedId, retry, user.id, appliedCode, quoteRetry, t]);

  useEffect(() => { if (review) reviewHeading.current?.focus(); }, [review]);
  useEffect(() => { if (created) successHeading.current?.focus(); }, [created]);

  async function purchase() {
    if (!selected || !validQuote || pending || ordersError || ordersLoading || submitting.current) return;
    submitting.current = true; setBusy(true); setPurchaseError('');
    try {
      // Keep the same key after a network error, so retrying cannot create a duplicate.
      const storageKey = `vo2.subscription-request.${user.id}.${selected.id}.${appliedCode || "base"}`;
      let key = requestKey.current;
      if (!key) {
        try { key = sessionStorage.getItem(storageKey); } catch { /* private browsing */ }
        key ??= `sub-${crypto.randomUUID()}`;
        requestKey.current = key;
        try { sessionStorage.setItem(storageKey, key); } catch { /* use in-memory key */ }
      }
      const order = await subscriptionApi.create(selected.id, key, appliedCode);
      if (![1, 2].includes(Number(order.status))) {
        requestKey.current = null;
        try { sessionStorage.removeItem(storageKey); } catch {}
        setPurchaseError(t('subscription.purchaseClosed'));
        return;
      }
      setCreated(order); setReview(false);
      if (Number(order.status) === 1 && onActivated) void onActivated().catch(() => {});
      setOrders(previous => [order, ...previous]);
      try { sessionStorage.removeItem(storageKey); } catch { /* private browsing */ }
      requestKey.current = null;
    } catch (error) {
      const reason = (error as { response?: { data?: { errors?: { discount_code?: string[] } } } })?.response?.data?.errors?.discount_code?.[0];
      if (appliedCode && reason) {
        setQuote(null); setReview(false);
        setCodeError(t(`subscription.couponErrors.${reason}`, { defaultValue: t('subscription.couponUnavailable') }));
      }
      setPurchaseError(t('subscription.purchaseError'));
      try { setOrders(await subscriptionApi.orders()); } catch { setOrdersError(true); }
    } finally { submitting.current = false; setBusy(false); }
  }

  const expiresAt = Number(created?.status) === 1 ? created?.ends_at ?? user.account_expire_date : user.account_expire_date;
  const expiry = expiresAt ? new Date(expiresAt) : null;
  const validExpiry = expiry && Number.isFinite(expiry.getTime());

  const active = Number(created?.status) === 1 || (user.is_subscription_active ?? Boolean(validExpiry && expiry.getTime() > Date.now()));

  return <div className="subscription-panel">
    <section className="subscription-status" aria-label={t('subscription.current')}>
      <IoShieldCheckmarkOutline aria-hidden="true" />
      <div><strong>{t('subscription.current')}</strong><span>{t(active ? 'subscription.active' : 'subscription.inactive')}</span></div>
      {validExpiry && <span className="subscription-expiry">{t('subscription.expires', { date: expiry.toLocaleDateString(locale) })}</span>}
    </section>

    {created ? <section className="subscription-success" role="status">
      <span className="subscription-success-icon"><IoCheckmarkOutline aria-hidden="true" /></span>
      <h3 ref={successHeading} tabIndex={-1}>{t(Number(created.status) === 1 ? 'subscription.freeActivated' : 'subscription.requestCreated')}</h3>
      <p>{t('subscription.requestNumber', { value: number(created.id) })}</p>
      <strong>{total(created.cash_amount, created.currency)}</strong>
      {Number(created.status) === 2 && deadline(created) && <p>{t('subscription.paymentDeadline', { date: deadline(created) })}</p>}
      <p>{t(Number(created.status) === 1 ? Number(created.cash_amount) === 0 ? 'subscription.freeSuccess' : 'subscription.activatedSuccess' : 'subscription.manualNote')}</p>
      {Number(created.status) !== 1 && <a className="nm-btn nm-btn--primary" href="https://t.me/vo2support" target="_blank" rel="noopener noreferrer">{t('subscription.contactSupport')}</a>}
    </section> : <>
      {pending && <section className="subscription-notice" role="status"><strong>{t('subscription.pendingTitle')}</strong><p>{t('subscription.pendingNote', { value: number(pending.id) })}</p>{deadline(pending) && <p>{t('subscription.paymentDeadline', { date: deadline(pending) })}</p>}<a href="https://t.me/vo2support" target="_blank" rel="noopener noreferrer">{t('subscription.contactSupport')}</a></section>}
      {loading ? <div className="subscription-loading" role="status"><NmLoader label={t('subscription.loading')} /></div>
        : loadError ? <div className="subscription-notice" role="alert"><p>{t('subscription.loadError')}</p><NmButton onClick={() => setRetry(r => r + 1)}>{t('subscription.retry')}</NmButton></div>
        : !plans.length ? <div className="subscription-notice"><p>{t('subscription.empty')}</p><a href="https://t.me/vo2support" target="_blank" rel="noopener noreferrer">{t('subscription.contactSupport')}</a></div>
        : <>
          <fieldset className="subscription-periods" disabled={busy}>
            <legend className="sr-only">{t('subscription.period')}</legend>
            <span className="subscription-period-indicator" aria-hidden="true" style={{ width: `${100 / periods.length}%`, transform: `translateX(${periods.indexOf(Number(selected?.day_count)) * 100}%)` }} />
            {periods.map(days => <label key={days}>
              <input type="radio" name="subscription-period" checked={Number(selected?.day_count) === days} onChange={() => {
                const next = plans.find(p => Number(p.day_count) === days && p.account_type === selected?.account_type) ?? plans.find(p => Number(p.day_count) === days);
                setSelectedId(next?.id ?? null);
              }} /><span>{duration(days)}</span>
            </label>)}
          </fieldset>

          <div className="subscription-pass" data-premium={selected?.account_type === 'premium'} aria-busy={quoteLoading}>
            <img className="subscription-pass-art" src={subscriptionCardArt} alt="" aria-hidden="true" width="756" height="1039" decoding="async" />
            <div className="subscription-pass-brand" aria-hidden="true">VO2<span>{t(role === 'coach' ? 'subscription.coach' : 'subscription.athlete')}</span></div>
            <div className="subscription-pass-center">
              <IoPeopleOutline aria-hidden="true" />
              <strong>{role === 'coach' ? validQuote ? validQuote.unlimited ? '∞' : number(validQuote.athlete_limit) : '—' : t('subscription.athletePass')}</strong>
              <span>{role === 'coach' ? t(validQuote?.unlimited ? 'subscription.unlimitedAthletes' : 'subscription.athletes') : t('subscription.personal')}</span>
            </div>
            <span className="subscription-pass-duration">{duration(Number(selected?.day_count))}</span>
          </div>

          <fieldset className="subscription-plans" disabled={busy}>
            <legend>{t('subscription.choosePlan')}</legend>
            {options.map(plan => <label key={plan.id} className="subscription-plan" data-selected={selectedId === plan.id}>
              <input type="radio" name="subscription-plan" value={plan.id} checked={selectedId === plan.id} onChange={() => setSelectedId(plan.id)} />
              <span><strong>{plan.display_name?.trim() || t('subscription.planName', { role: t(role === 'coach' ? 'subscription.coach' : 'subscription.athlete'), tier: t(plan.account_type === 'premium' ? role === 'coach' && plan.unlimited !== false ? 'subscription.unlimited' : 'subscription.premium' : 'subscription.basic') })}</strong><small>{duration(Number(plan.day_count))}</small></span>
              <IoCheckmarkOutline aria-hidden="true" />
            </label>)}
          </fieldset>

          <form className="subscription-coupon" onSubmit={event => { event.preventDefault(); setReview(false); if (appliedCode === normalizeCode(codeDraft)) setQuoteRetry(r => r + 1); else setAppliedCode(normalizeCode(codeDraft)); }}>
            <label htmlFor="subscription-discount-code">{t('subscription.couponLabel')}</label>
            <div className="subscription-coupon-input">
              <input id="subscription-discount-code" type="text" autoComplete="off" autoCapitalize="characters" spellCheck={false}
                dir="ltr" maxLength={48} value={codeDraft} placeholder={t('subscription.couponPlaceholder')}
                aria-invalid={!!codeError} aria-describedby="subscription-coupon-help" disabled={busy || !!pending}
                onChange={event => setCodeDraft(event.target.value)} />
              <NmButton type="submit" disabled={busy || quoteLoading || !!pending || !normalizeCode(codeDraft) || (appliedCode === normalizeCode(codeDraft) && !codeError)}>{t('subscription.couponApply')}</NmButton>
            </div>
            <p id="subscription-coupon-help">{t('subscription.couponHint')}</p>
            {appliedCode && <div className="subscription-coupon-status">
              <span>{validQuote && !quoteLoading ? <>{t('subscription.couponApplied')} <bdi>{appliedCode}</bdi></> : appliedCode}</span>
              <NmButton type="button" size="sm" variant="ghost" disabled={busy || !!pending} onClick={() => { setAppliedCode(''); setCodeDraft(''); setCodeError(''); }}>{t('subscription.couponRemove')}</NmButton>
            </div>}
            {codeError && <p className="subscription-error" role="alert">{codeError}</p>}
          </form>

          {quoteError && <div className="subscription-notice" role="alert"><p>{t('subscription.quoteError')}</p><NmButton onClick={() => setRetry(r => r + 1)}>{t('subscription.retry')}</NmButton> <a href="https://t.me/vo2support" target="_blank" rel="noopener noreferrer">{t('subscription.contactSupport')}</a></div>}
          {ordersError && <div className="subscription-notice" role="alert"><p>{t('subscription.ordersError')}</p><NmButton onClick={() => setRetry(r => r + 1)}>{t('subscription.retry')}</NmButton></div>}

          <section className="subscription-summary" aria-busy={quoteLoading}>
            {review && <h3 ref={reviewHeading} tabIndex={-1}>{t('subscription.review')}</h3>}
            <dl>
              <div><dt>{t('subscription.period')}</dt><dd>{duration(Number(selected?.day_count))}</dd></div>
              {validQuote && <>
                <div><dt>{t('subscription.base')}</dt><dd>{total(validQuote.base_amount, validQuote.currency)}</dd></div>
                {validQuote.discount_amount > 0 && <div><dt>{t('subscription.discount')}</dt><dd>−{total(validQuote.discount_amount, validQuote.currency)}</dd></div>}
              </>}
              <div className="subscription-total"><dt>{t('subscription.total')}</dt><dd>{validQuote ? total(validQuote.cash_amount, validQuote.currency) : quoteLoading ? <NmLoader size="xs" inline label={t('subscription.calculating')} /> : '—'}</dd></div>
            </dl>
            {purchaseError && <p role="alert" className="subscription-error">{purchaseError}</p>}
            <NmButton variant="primary" size="lg" className="subscription-cta" loading={busy}
              disabled={!validQuote || quoteLoading || !!pending || ordersError || ordersLoading}
              onClick={() => review ? void purchase() : setReview(true)}>{t(review ? validQuote?.cash_amount === 0 ? 'subscription.activateFree' : 'subscription.submit' : 'subscription.continue')}</NmButton>
            {review && !busy && <NmButton variant="ghost" className="subscription-cta" onClick={() => setReview(false)}>{t('subscription.edit')}</NmButton>}
          </section>
        </>}
    </>}
  </div>;
}
